import { collection, doc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot, updateDoc } from '@/lib/metered'
import { db } from '@/lib/firebase'
import { recordChange } from '@/lib/activity'
import { useSharedCollection } from '@/lib/sharedCollection'
import type { Owner } from './types'

/** `enabled` mirrors `useDecisions` — see the note there. The global search needs it. */
export function useOwners(enabled = true) {
  const { rows, loading, error } = useSharedCollection<Owner>(
    'owners',
    (onRows, onError) => onSnapshot(
      collection(db, 'owners'),
      (snap) => onRows(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as Owner)
          .sort((a, b) => a.order - b.order),
      ),
      onError,
    ),
    enabled,
  )
  return { owners: rows, loading, error }
}

export type OwnerEdit = Partial<Pick<Owner, 'name' | 'email' | 'status' | 'notes'>>

const LABELS: Record<string, string> = {
  name: 'Name', email: 'Email', status: 'Status', notes: 'Notes',
}

export async function saveOwner(
  owner: Owner,
  edit: OwnerEdit,
  actor: { email: string; name?: string | null },
) {
  await updateDoc(doc(db, 'owners', owner.id), {
    ...edit,
    confirmedBy: actor.email,
    confirmedAt: serverTimestamp(),
  })

  // One entry per field that actually moved. "Asked → Confirmed" with a date on it is the
  // whole point: six weeks from now, who agreed to what and when is not a memory question.
  for (const [key, value] of Object.entries(edit) as Array<[keyof OwnerEdit, string]>) {
    const before = (owner[key] ?? '') as string
    if (value === before) continue
    await recordChange(['owners', owner.id], {
      field: LABELS[key] ?? key,
      from: before || '(nobody)',
      to: value || '(cleared)',
    }, actor)
  }
}
